import { CloseCircleFilled, EditOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { configurationApi } from "api/configuration.api";
import { AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Configuration } from "types/configuration";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { ConfigurationModal } from "./components/ConfigurationModal";
import {
  CONFIGURATION_PATH,
  configurationDataTypeLabel,
  configurationParamLabel,
} from "./configuration.meta";

export const ConfigurationPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Configuration[]>([]);
  const [total, setTotal] = useState(0);
  const configurationModalRef = useRef<ConfigurationModal>(null);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await configurationApi.findAll(queryParams);
      setData(res.data.configurations || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const canEdit = can(CONFIGURATION_PATH, "UPDATE");

  const columns = useMemo<TableColumnsType<Configuration>>(
    () => [
      {
        title: "Tham số",
        dataIndex: "param",
        width: 220,
        render: (param: string) => <TableContent>{configurationParamLabel[param] || param || "--"}</TableContent>,
      },
      {
        title: "Giá trị",
        dataIndex: "value",
        render: (value: string, record: Configuration) => {
          if (record.dataType === "BOOLEAN") {
            return <TableContent>{value === "true" ? "Bật" : "Tắt"}</TableContent>;
          }
          return <TableContent>{value || "--"}</TableContent>;
        },
      },
      {
        title: "Kiểu dữ liệu",
        dataIndex: "dataType",
        width: 160,
        render: (dataType: string) => (
          <TableContent>{configurationDataTypeLabel[dataType] || dataType || "--"}</TableContent>
        ),
      },
      ...(canEdit
        ? [
            {
              title: "Thao tác",
              key: "action",
              width: 80,
              align: "center" as const,
              render: (_: unknown, record: Configuration) => (
                <div className="flex justify-center items-center gap-2">
                  <Tooltip title="Cập nhật">
                    <ActionButton
                      aria-label="Cập nhật"
                      onClick={() => {
                        configurationModalRef.current?.handleUpdate(record);
                      }}
                    >
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 320,
              placeholder: "Tìm theo tham số, giá trị",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            filterLabel={null}
          />
        }
        table={
          <AppTable<Configuration>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <ConfigurationModal
        onSubmitOk={() => fetchData(query)}
        onClose={() => {}}
        ref={configurationModalRef}
      />
    </div>
  );
});
